import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, Copy, Download, QrCode, Stethoscope, X } from "lucide-react";

const TONE = {
  pending: "border-amber-500/50 text-amber-700 bg-amber-500/10",
  approved: "border-emerald-500/40 text-emerald-700 bg-emerald-50",
  rejected: "border-border text-muted-foreground",
};

const money = (v) => `₹${Number(v || 0).toFixed(0)}`;

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 text-sm py-1.5 border-b border-border/60">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right">{value || "—"}</span>
  </div>
);

const PartnerDetail = ({ id, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [payment, setPayment] = useState({ amount: "", note: "", paid_on: "" });

  const load = () => api.get(`/admin/doctor-partners/${id}`).then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const act = async (path, body, message) => {
    try {
      await api.post(`/admin/doctor-partners/${id}/${path}`, body || {});
      toast.success(message);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadQr = async () => {
    try {
      const { data: blob } = await api.get(`/admin/doctor-partners/${id}/qr.png`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${data.partner.code}-qr.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addPayment = async () => {
    if (!Number(payment.amount)) return toast.error("Enter the amount paid");
    try {
      await api.post(`/admin/doctor-partners/${id}/payments`, {
        amount: Number(payment.amount), note: payment.note, paid_on: payment.paid_on,
      });
      setPayment({ amount: "", note: "", paid_on: "" });
      toast.success("Payment recorded");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return null;
  const p = data.partner;
  const pending = p.pending_profile || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4"
         onClick={onClose} data-testid="partner-detail">
      <Card className="max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-6 lg:p-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-display text-2xl">{p.name}</h3>
              <p className="text-sm text-muted-foreground">{p.degree} · {p.council_no}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={TONE[p.status]} data-testid="partner-detail-status">{p.status}</Badge>
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="partner-detail-close"><X size={16} /></Button>
            </div>
          </div>

          <div className="mb-5">
            <Row label="Clinic" value={p.clinic_name} />
            <Row label="Address" value={p.clinic_address} />
            <Row label="Mobile" value={p.phone} />
            <Row label="Email" value={p.email} />
            <Row label="Applied" value={new Date(p.created_at).toLocaleString("en-IN")} />
            {p.bio && <Row label="Bio" value={p.bio} />}
            {p.rejection_reason && <Row label="Note" value={p.rejection_reason} />}
          </div>

          {p.code && (
            <div className="rounded-xl border border-border p-4 mb-5" data-testid="partner-link-block">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono">{p.code}</Badge>
                <code className="text-xs bg-muted/60 rounded-lg px-2 py-1 truncate max-w-[300px]">{p.link}</code>
                <Button size="sm" variant="outline" className="btn-pill h-8"
                        onClick={() => { navigator.clipboard?.writeText(p.link); toast.success("Link copied"); }}
                        data-testid="partner-copy-link">
                  <Copy size={12} className="mr-1.5" /> Copy
                </Button>
                <Button size="sm" variant="outline" className="btn-pill h-8" onClick={downloadQr}
                        data-testid="partner-download-qr">
                  <Download size={12} className="mr-1.5" /> QR code
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {[["Scans", data.stats.scans], ["Prescriptions", data.stats.prescriptions],
                  ["Appointments", data.stats.appointments], ["Revenue", money(data.stats.revenue)]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-border p-2">
                    <div className="overline">{k}</div>
                    <div className="font-display text-lg">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.profile_status === "pending_review" && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 mb-5" data-testid="partner-profile-review">
              <p className="text-sm font-medium mb-2">Profile changes waiting for you</p>
              {Object.entries(pending).map(([key, value]) => (
                <Row key={key} label={key.replace(/_/g, " ")} value={String(value).slice(0, 200)} />
              ))}
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="btn-pill h-9"
                        onClick={() => act("profile-review?approve=true", {}, "Profile updated")}
                        data-testid="partner-profile-approve">
                  <Check size={13} className="mr-1.5" /> Approve changes
                </Button>
                <Button size="sm" variant="outline" className="btn-pill h-9"
                        onClick={() => act("profile-review?approve=false", { reason }, "Changes declined")}
                        data-testid="partner-profile-reject">
                  Decline
                </Button>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border p-4 mb-5" data-testid="partner-payments-block">
            <p className="text-sm font-medium mb-1">Payments made to this doctor</p>
            <p className="text-xs text-muted-foreground mb-3">
              Entered by hand — nothing is calculated or paid out automatically.
            </p>
            {(data.payments || []).map((entry) => (
              <Row key={entry.id} label={`${entry.paid_on} · ${entry.note || "—"}`} value={money(entry.amount)} />
            ))}
            <div className="grid sm:grid-cols-4 gap-2 mt-3">
              <Input value={payment.amount} placeholder="Amount ₹" className="bg-transparent"
                     onChange={(e) => setPayment((s) => ({ ...s, amount: e.target.value.replace(/[^\d.]/g, "") }))}
                     data-testid="partner-payment-amount" />
              <Input value={payment.paid_on} placeholder="YYYY-MM-DD" className="bg-transparent"
                     onChange={(e) => setPayment((s) => ({ ...s, paid_on: e.target.value }))}
                     data-testid="partner-payment-date" />
              <Input value={payment.note} placeholder="Note" className="bg-transparent sm:col-span-2"
                     onChange={(e) => setPayment((s) => ({ ...s, note: e.target.value }))}
                     data-testid="partner-payment-note" />
            </div>
            <Button size="sm" variant="outline" className="btn-pill h-9 mt-2" onClick={addPayment}
                    data-testid="partner-payment-add">
              Record payment
            </Button>
          </div>

          {p.status !== "approved" && (
            <div className="flex flex-wrap items-center gap-2">
              <Button className="btn-action h-10" onClick={() => act("approve", {}, "Approved — referral code issued")}
                      data-testid="partner-approve">
                <Check size={14} className="mr-1.5" /> Approve & issue code
              </Button>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
                     className="w-48 bg-transparent" data-testid="partner-reject-reason" />
              <Button variant="outline" className="btn-pill h-10 text-destructive"
                      onClick={() => act("reject", { reason }, "Application rejected")}
                      data-testid="partner-reject">
                Reject
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Admin → Doctor Partners: applications in, codes out. */
export const DoctorPartnersTab = () => {
  const [data, setData] = useState({ partners: [], counts: {}, statuses: [] });
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);

  const load = () =>
    api.get("/admin/doctor-partners", { params: { status } })
      .then(({ data: body }) => setData(body))
      .catch(() => {});
  useEffect(() => { load(); }, [status]);

  return (
    <Card className="border-border" data-testid="doctor-partners-tab">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="font-display text-2xl flex items-center gap-2"><Stethoscope size={18} /> Doctor partners</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Applications from /doctor-partner. A referral code and QR are issued on approval.
            </p>
          </div>
          <Badge variant="outline" data-testid="partners-total">{data.counts?.total || 0} total</Badge>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", ...(data.statuses || [])].map((option) => (
            <Button key={option} size="sm" variant={status === option ? "default" : "outline"}
                    className="btn-pill h-8 capitalize text-xs" onClick={() => setStatus(option)}
                    data-testid={`partners-filter-${option}`}>
              {option}{option !== "all" && data.counts?.[option] ? ` (${data.counts[option]})` : ""}
            </Button>
          ))}
        </div>

        {data.partners.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="partners-empty">
            No applications here yet. Share /doctor-partner with the doctors you work with.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-normal">Doctor</th>
                  <th className="py-2 pr-4 font-normal">Clinic</th>
                  <th className="py-2 pr-4 font-normal">Code</th>
                  <th className="py-2 pr-4 font-normal">Status</th>
                  <th className="py-2 font-normal" />
                </tr>
              </thead>
              <tbody>
                {data.partners.map((row) => (
                  <tr key={row.id} className="border-b border-border/60" data-testid={`partner-row-${row.id}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium">{row.name}</div>
                      <div className="text-xs text-muted-foreground">{row.phone} · {row.email}</div>
                    </td>
                    <td className="py-3 pr-4">{row.clinic_name || "—"}</td>
                    <td className="py-3 pr-4 font-mono text-xs">{row.code || "—"}</td>
                    <td className="py-3 pr-4">
                      <Badge variant="outline" className={`${TONE[row.status]} text-[10px] capitalize`}>
                        {row.status}
                      </Badge>
                      {row.profile_status === "pending_review" && (
                        <Badge variant="outline" className="ml-1 text-[10px] border-amber-500/50 text-amber-700">
                          profile edit
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      <Button size="sm" variant="outline" className="btn-pill h-8" onClick={() => setOpen(row.id)}
                              data-testid={`partner-open-${row.id}`}>
                        {row.status === "pending" ? "Review" : "Open"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {open && <PartnerDetail id={open} onClose={() => setOpen(null)} onChanged={load} />}
      </CardContent>
    </Card>
  );
};

export default DoctorPartnersTab;
